import { Routes } from "@angular/router";
import {
  MobileShellComponent,
  canMatchEngagementPublicationPlayerOnNonMobileHost,
  canMatchMobileEngagementSlugWebCanonicalRedirect,
  mobileAuthGuard,
} from "@kiraah-shared/mobile-skeleton";

const loadAppModulePlaceholder = () =>
  import("./pages/shared/app-module-placeholder/app-module-placeholder.component").then(
    (moduleExports) => moduleExports.AppModulePlaceholderComponent
  );

/** Deep links under app domains (menu URLs) resolve to placeholder until features ship. */
const appDomainPlaceholderRoutes: Routes = ["econformite"].map((segment) => ({
  path: segment,
  children: [{ path: "**", loadComponent: loadAppModulePlaceholder }],
}));

const eauditRoutes: Routes = [
  { path: "", pathMatch: "full", redirectTo: "missions" },
  {
    path: "missions",
    loadChildren: () =>
      import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedMissionTaskRoutes),
  },
  { path: "tasks", pathMatch: "full", redirectTo: "missions" },
  { path: "signature", redirectTo: "/mobile/signature", pathMatch: "full" },
  { path: "**", loadComponent: loadAppModulePlaceholder },
];

const eachatRoutes: Routes = [
  { path: "", pathMatch: "full", redirectTo: "demandes" },
  { path: "tickets/list", pathMatch: "full", redirectTo: "demandes" },
  { path: "tickets", pathMatch: "full", redirectTo: "demandes" },
  { path: "ticket/list", pathMatch: "full", redirectTo: "demandes" },
  {
    path: "demandes",
    loadChildren: () =>
      import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedTicketDemandRoutes),
  },
  {
    path: "tickets/voiceaudio",
    loadComponent: () =>
      import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.MobileEachatVoiceDemandPageComponent),
  },
  { path: "**", loadComponent: loadAppModulePlaceholder },
];

const equipmentMobileRoutes: Routes = [
  { path: "", pathMatch: "full", redirectTo: "vehicles" },
  { path: "vehicle/list", pathMatch: "full", redirectTo: "vehicles" },
  { path: "vehicle/maintenance/list", pathMatch: "full", redirectTo: "maintenance" },
  { path: "vehicle/assignment/list", pathMatch: "full", redirectTo: "assignations" },
  { path: "vehicle/kilometer/list", pathMatch: "full", redirectTo: "kilometrage" },
  { path: "vehicle/traffic-ticket/list", pathMatch: "full", redirectTo: "contraventions" },
  {
    path: "kilometrage",
    loadComponent: () =>
      import("./pages/equipment/mobile-equipment-mileage-list-page/mobile-equipment-mileage-list.component").then(
        (moduleExports) => moduleExports.MobileEquipmentMileageListPageComponent,
      ),
  },
  {
    path: "vehicles",
    loadComponent: () =>
      import("./pages/equipment/mobile-equipment-list-page/mobile-equipment-list.component").then(
        (moduleExports) => moduleExports.MobileEquipmentListPageComponent,
      ),
  },
  {
    path: "vehicles/:equipmentUid",
    loadComponent: () =>
      import("./pages/equipment/mobile-equipment-detail-page/mobile-equipment-detail.component").then(
        (moduleExports) => moduleExports.MobileEquipmentDetailPageComponent,
      ),
  },
  {
    path: "vehicles/:equipmentUid/kilometrage",
    loadComponent: () =>
      import("./pages/equipment/mobile-equipment-mileage-list-page/mobile-equipment-mileage-list.component").then(
        (moduleExports) => moduleExports.MobileEquipmentMileageListPageComponent,
      ),
  },
  {
    path: "vehicles/:equipmentUid/kilometrage/:mileageUid",
    loadComponent: () =>
      import("./pages/equipment/mobile-equipment-mileage-detail-page/mobile-equipment-mileage-detail.component").then(
        (moduleExports) => moduleExports.MobileEquipmentMileageDetailPageComponent,
      ),
  },

  // Equipment assignments
  {
    path: "assignations",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-assignment-list-page/mobile-equipment-assignment-list.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentAssignmentListPageComponent),
  },
  {
    path: "assignations/:assignmentUid",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-assignment-detail-page/mobile-equipment-assignment-detail.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentAssignmentDetailPageComponent),
  },
  {
    path: "vehicles/:equipmentUid/assignations",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-assignment-list-page/mobile-equipment-assignment-list.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentAssignmentListPageComponent),
  },
  {
    path: "vehicles/:equipmentUid/assignations/:assignmentUid",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-assignment-detail-page/mobile-equipment-assignment-detail.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentAssignmentDetailPageComponent),
  },

  // Equipment maintenances
  {
    path: "maintenance",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-maintenance-list-page/mobile-equipment-maintenance-list.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentMaintenanceListPageComponent),
  },
  {
    path: "maintenance/:maintenanceUid",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-maintenance-detail-page/mobile-equipment-maintenance-detail.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentMaintenanceDetailPageComponent),
  },
  {
    path: "vehicles/:equipmentUid/maintenance",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-maintenance-list-page/mobile-equipment-maintenance-list.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentMaintenanceListPageComponent),
  },
  {
    path: "vehicles/:equipmentUid/maintenance/:maintenanceUid",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-maintenance-detail-page/mobile-equipment-maintenance-detail.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentMaintenanceDetailPageComponent),
  },

  // Equipment contraventions
  {
    path: "contraventions",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-contravention-list-page/mobile-equipment-contravention-list.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentContraventionListPageComponent),
  },
  {
    path: "contraventions/:contraventionUid",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-contravention-detail-page/mobile-equipment-contravention-detail.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentContraventionDetailPageComponent),
  },
  {
    path: "vehicles/:equipmentUid/contraventions",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-contravention-list-page/mobile-equipment-contravention-list.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentContraventionListPageComponent),
  },
  {
    path: "vehicles/:equipmentUid/contraventions/:contraventionUid",
    loadComponent: () =>
      import(
        "./pages/equipment/mobile-equipment-contravention-detail-page/mobile-equipment-contravention-detail.component"
      ).then((moduleExports) => moduleExports.MobileEquipmentContraventionDetailPageComponent),
  },

  // Drivers (global module, no equipment scope)
  { path: "drivers", pathMatch: "full", redirectTo: "chauffeurs" },
  { path: "driver/list", pathMatch: "full", redirectTo: "chauffeurs" },
  { path: "driver", pathMatch: "full", redirectTo: "chauffeurs" },
  {
    path: "chauffeurs",
    loadComponent: () =>
      import("./pages/equipment/mobile-driver-list-page/mobile-driver-list.component").then(
        (moduleExports) => moduleExports.MobileDriverListPageComponent,
      ),
  },
  {
    path: "chauffeurs/:driverUid",
    loadComponent: () =>
      import("./pages/equipment/mobile-driver-detail-page/mobile-driver-detail.component").then(
        (moduleExports) => moduleExports.MobileDriverDetailPageComponent,
      ),
  },

  { path: "**", loadComponent: loadAppModulePlaceholder },
];

/** Hub E-Chantier : pas d’écran « liste chantiers » — même liste missions que le BTP (`/mobile/chantier/missions`, 4 actions). */
const echantierRoutes: Routes = [
  { path: "", pathMatch: "full", redirectTo: "/mobile/chantier/missions" },
  { path: "construction-site", pathMatch: "full", redirectTo: "/mobile/chantier/missions" },
  { path: "construction-sites", pathMatch: "full", redirectTo: "/mobile/chantier/missions" },
  { path: "tasks", pathMatch: "full", redirectTo: "/mobile/chantier/missions" },
  {
    path: "time-tracking",
    loadComponent: () =>
      import("./pages/construction-site/mobile-construction-site-time-tracking/mobile-construction-site-time-tracking.component").then(
        (moduleExports) => moduleExports.MobileConstructionSiteTimeTrackingComponent
      ),
  },
  {
    path: "chat",
    loadComponent: () =>
      import("./pages/construction-site/mobile-construction-site-chat/mobile-construction-site-chat.component").then(
        (moduleExports) => moduleExports.MobileConstructionSiteChatComponent
      ),
  },
  {
    path: "cra",
    loadComponent: () =>
      import("./pages/construction-site/mobile-construction-site-cra/mobile-construction-site-cra.component").then(
        (moduleExports) => moduleExports.MobileConstructionSiteCraComponent
      ),
  },
  {
    path: "documents",
    loadComponent: () =>
      import("./pages/construction-site/mobile-construction-site-documents/mobile-construction-site-documents.component").then(
        (moduleExports) => moduleExports.MobileConstructionSiteDocumentsComponent
      ),
  },
  { path: "signature", redirectTo: "/mobile/signature", pathMatch: "full" },
  {
    path: "security-quiz",
    loadComponent: () =>
      import("./pages/construction-site/mobile-construction-site-security-quiz/mobile-construction-site-security-quiz.component").then(
        (moduleExports) => moduleExports.MobileConstructionSiteSecurityQuizComponent
      ),
  },
  {
    path: "planning",
    loadComponent: () =>
      import("./pages/construction-site/mobile-construction-site-planning/mobile-construction-site-planning.component").then(
        (moduleExports) => moduleExports.MobileConstructionSitePlanningComponent
      ),
  },
  {
    path: "alerts",
    loadComponent: () =>
      import("./pages/construction-site/mobile-construction-site-alerts/mobile-construction-site-alerts.component").then(
        (moduleExports) => moduleExports.MobileConstructionSiteAlertsComponent
      ),
  },
  {
    path: "fees",
    loadComponent: () =>
      import("./pages/construction-site/mobile-construction-site-fees/mobile-construction-site-fees.component").then(
        (moduleExports) => moduleExports.MobileConstructionSiteFeesComponent
      ),
  },
  {
    path: "memo",
    loadComponent: () =>
      import("./pages/construction-site/mobile-construction-site-memo/mobile-construction-site-memo.component").then(
        (moduleExports) => moduleExports.MobileConstructionSiteMemoComponent
      ),
  },
  {
    path: "absences",
    loadComponent: () =>
      import("./pages/construction-site/mobile-construction-site-absences/mobile-construction-site-absences.component").then(
        (moduleExports) => moduleExports.MobileConstructionSiteAbsencesComponent
      ),
  },
  { path: "**", loadComponent: loadAppModulePlaceholder },
];

const mobileShellChildren: Routes = [
  {
    path: "home",
    loadComponent: () =>
      import("./pages/home/mobile-home.component").then((moduleExports) => moduleExports.MobileHomeComponent),
  },
  {
    path: "launcher",
    loadComponent: () =>
      import("./pages/launcher/mobile-app-launcher/mobile-app-launcher.component").then(
        (moduleExports) => moduleExports.MobileAppLauncherComponent
      ),
  },
  {
    path: "elearning",
    children: [
      { path: "student/sessions/list", pathMatch: "full", redirectTo: "student/sessions" },
      { path: "mentor/sessions/list", pathMatch: "full", redirectTo: "mentor/sessions" },
      { path: "sessions/list", pathMatch: "full", redirectTo: "sessions" },
      { path: "student/surveys/list", pathMatch: "full", redirectTo: "student/surveys" },
      { path: "mentor/surveys/list", pathMatch: "full", redirectTo: "mentor/surveys" },
      { path: "contact/sessions/list", pathMatch: "full", redirectTo: "contact/sessions" },
      { path: "contact/surveys/list", pathMatch: "full", redirectTo: "contact/surveys" },
      { path: "supervisor/sessions/list", pathMatch: "full", redirectTo: "supervisor/sessions" },
      { path: "supervisor/surveys/list", pathMatch: "full", redirectTo: "supervisor/surveys" },
      {
        path: "sessions",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedHubTrainingSessionRoutes),
      },
      {
        path: "student/sessions",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedStudentTrainingSessionRoutes),
      },
      {
        path: "student/tasks",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedStudentPortalTasksRoutes),
      },
      {
        path: "student/documents",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then(
            (moduleExports) => moduleExports.authenticatedStudentPortalDocumentsRoutes
          ),
      },
      {
        path: "mentor/sessions",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedMentorTrainingSessionRoutes),
      },
      {
        path: "mentor/tasks",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedMentorPortalTasksRoutes),
      },
      {
        path: "mentor/documents",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then(
            (moduleExports) => moduleExports.authenticatedMentorPortalDocumentsRoutes
          ),
      },
      {
        path: "student/surveys",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedStudentLearningCampaignRoutes),
      },
      {
        path: "mentor/surveys",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedMentorLearningCampaignRoutes),
      },
      {
        path: "contact/sessions",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedContactTrainingSessionRoutes),
      },
      {
        path: "contact/tasks",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedContactPortalTasksRoutes),
      },
      {
        path: "contact/documents",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then(
            (moduleExports) => moduleExports.authenticatedContactPortalDocumentsRoutes
          ),
      },
      {
        path: "contact/surveys",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedContactLearningCampaignRoutes),
      },
      {
        path: "supervisor/sessions",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then(
            (moduleExports) => moduleExports.authenticatedSupervisorTrainingSessionRoutes
          ),
      },
      {
        path: "supervisor/tasks",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then(
            (moduleExports) => moduleExports.authenticatedSupervisorPortalTasksRoutes
          ),
      },
      {
        path: "supervisor/documents",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then(
            (moduleExports) => moduleExports.authenticatedSupervisorPortalDocumentsRoutes
          ),
      },
      {
        path: "supervisor/surveys",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then(
            (moduleExports) => moduleExports.authenticatedSupervisorLearningCampaignRoutes
          ),
      },
      {
        path: "",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedElearningPlayerRoutes),
      },
    ],
  },
  { path: "quiz", redirectTo: "elearning", pathMatch: "prefix" },
  { path: "echantier", children: echantierRoutes },
  {
    path: "chantier",
    children: [
      {
        path: "missions",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedMissionTaskChantierRoutes),
      },
    ],
  },
  { path: "tasks", pathMatch: "full", redirectTo: "eaudit/missions" },
  { path: "tasks/:missionUid", redirectTo: "eaudit/missions/:missionUid" },
  { path: "tasks/:missionUid/info", redirectTo: "eaudit/missions/:missionUid/info" },
  { path: "tasks/:missionUid/voice-report", redirectTo: "eaudit/missions/:missionUid/voice-report" },
  { path: "tasks/:missionUid/t/:taskUid", redirectTo: "eaudit/missions/:missionUid/t/:taskUid" },
  {
    path: "tasks/:missionUid/t/:taskUid/chat",
    redirectTo: "eaudit/missions/:missionUid/t/:taskUid/chat",
  },
  { path: "eaudit", children: eauditRoutes },
  {
    path: "eprojet",
    children: [
      { path: "", pathMatch: "full", redirectTo: "projets" },
      {
        path: "projets",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedMissionTaskProjectRoutes),
      },
      { path: "missions", pathMatch: "full", redirectTo: "projets" },
      { path: "missions/:missionUid", redirectTo: "projets/:missionUid" },
      { path: "missions/:missionUid/info", redirectTo: "projets/:missionUid/info" },
      { path: "missions/:missionUid/voice-report", redirectTo: "projets/:missionUid/voice-report" },
      { path: "missions/:missionUid/t/:taskUid", redirectTo: "projets/:missionUid/t/:taskUid" },
      {
        path: "missions/:missionUid/t/:taskUid/chat",
        redirectTo: "projets/:missionUid/t/:taskUid/chat",
      },
      { path: "**", loadComponent: loadAppModulePlaceholder },
    ],
  },
  {
    path: "profile",
    loadComponent: () =>
      import("./pages/profile/mobile-profile.component").then((moduleExports) => moduleExports.MobileProfileComponent),
  },
  {
    path: "menu/:menuUid",
    loadComponent: () =>
      import("./pages/menu/mobile-menu-items-list/mobile-menu-items-list.component").then(
        (moduleExports) => moduleExports.MobileMenuItemsListComponent
      ),
  },
  {
    path: "signature",
    loadChildren: () =>
      import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedSignatureRoutes),
  },
  {
    path: "presence",
    loadChildren: () =>
      import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.authenticatedPresenceRoutes),
  },
  {
    path: "module-unavailable",
    loadComponent: loadAppModulePlaceholder,
  },
  {
    path: "eequipement",
    children: equipmentMobileRoutes,
  },
  {
    path: "eachat",
    children: eachatRoutes,
  },
  ...appDomainPlaceholderRoutes,
  {
    path: "",
    pathMatch: "full",
    redirectTo: "launcher",
  },
  {
    path: "**",
    loadComponent: () =>
      import("./pages/shared/app-module-placeholder/app-module-placeholder.component").then(
        (moduleExports) => moduleExports.AppModulePlaceholderComponent
      ),
  },
];

export const routes: Routes = [
  { path: "", pathMatch: "full", redirectTo: "mobile/launcher" },
  {
    path: "e-signature/public",
    loadChildren: () =>
      import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.publicSignatureRoutes),
  },
  {
    path: "e-signature/gateway/:token",
    loadComponent: () =>
      import("@kiraah-shared/mobile-skeleton").then(
        (moduleExports) => moduleExports.MobileSignaturePublicGatewayComponent
      ),
  },
  {
    path: "presence/gateway/:token",
    loadComponent: () =>
      import("@kiraah-shared/mobile-skeleton").then(
        (moduleExports) => moduleExports.MobilePresencePublicGatewayComponent
      ),
  },
  {
    path: "presence/public",
    loadChildren: () =>
      import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.publicPresenceRoutes),
  },
  {
    path: "presence/sign",
    loadChildren: () =>
      import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.publicPresenceRoutes),
  },
  {
    path: "learning-campaign/gateway/:token",
    loadComponent: () =>
      import("@kiraah-shared/mobile-skeleton").then(
        (moduleExports) => moduleExports.MobileLearningCampaignPublicGatewayComponent
      ),
  },
  {
    path: "learning-campaign/public",
    loadChildren: () =>
      import("@kiraah-shared/mobile-skeleton").then(
        (moduleExports) => moduleExports.publicLearningCampaignRoutes
      ),
  },
  {
    path: "engagement/gateway/:token",
    loadComponent: () =>
      import("@kiraah-shared/mobile-skeleton").then(
        (moduleExports) => moduleExports.MobilePublicEngagementPublicGatewayComponent
      ),
  },
  {
    path: "engagement/public",
    loadChildren: () =>
      import("@kiraah-shared/mobile-skeleton").then(
        (moduleExports) => moduleExports.publicEngagementRoutes
      ),
  },
  {
    path: "engagement/:accessKey/:slug/sequence/:sequenceUid/player/activity/:activityUid/items",
    canMatch: [canMatchEngagementPublicationPlayerOnNonMobileHost],
    loadComponent: () =>
      import("@kiraah-shared/daily-activity").then((m) => m.ActivityItemPlayerViewComponent),
  },
  {
    path: "engagement/:accessKey/:slug/result",
    canMatch: [canMatchEngagementPublicationPlayerOnNonMobileHost],
    loadComponent: () =>
      import("@kiraah-shared/academy").then((m) => m.PublicEngagementAnonymousResultComponent),
  },
  {
    path: "engagement/:accessKey/:slug",
    canMatch: [canMatchMobileEngagementSlugWebCanonicalRedirect],
    loadComponent: () =>
      import("@kiraah-shared/mobile-skeleton").then(
        (moduleExports) => moduleExports.MobilePublicEngagementSlugWebRedirectComponent
      ),
  },
  {
    path: "engagement/:accessKey/:slug",
    canMatch: [canMatchEngagementPublicationPlayerOnNonMobileHost],
    loadComponent: () =>
      import("@kiraah-shared/academy").then((m) => m.PublicEngagementPublicPlayerComponent),
  },
  {
    path: "mobile",
    children: [
      {
        path: "auth/login",
        loadComponent: () =>
          import("./pages/auth/mobile-login/mobile-login.component").then((moduleExports) => moduleExports.MobileLoginComponent),
      },
      {
        path: "auth/tenant-selection",
        loadComponent: () =>
          import("./pages/auth/mobile-tenant-selection/mobile-tenant-selection.component").then(
            (moduleExports) => moduleExports.MobileTenantSelectionComponent
          ),
      },
      {
        path: "public/signature",
        loadChildren: () =>
          import("@kiraah-shared/mobile-skeleton").then((moduleExports) => moduleExports.publicSignatureRoutes),
      },
      {
        path: "",
        canActivate: [mobileAuthGuard],
        component: MobileShellComponent,
        children: mobileShellChildren,
      },
    ],
  },
  { path: "auth/login", redirectTo: "mobile/auth/login", pathMatch: "full" },
  { path: "launcher", redirectTo: "mobile/launcher", pathMatch: "full" },
  { path: "**", redirectTo: "mobile/launcher" },
];
